import { AuthHelper } from "@/components/pages/Auth/AuthHelper";
import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import { OutlineButton } from "@/components/StoreComponents/StoreButton";
import { CheckoutToggle } from "@/components/StoreComponents/StoreToggle";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch } from "@/redux/hooks";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "@/utils/StringUtils";
import { Box, styled, useTheme } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CheckoutInputV2 } from "../../../CheckoutInputV2";
import { CheckoutButton } from "./CheckoutButton";
import { SuccessStatusIcon } from "./SuccessStatusIcon";

// Style for the Send Code button to match the mock
const SendCodeButton = styled(CheckoutButton)(({ theme }) => ({
  borderRadius: "20px",
  padding: "8px 16px",
  fontWeight: 500,
  fontSize: "14px",
  textTransform: "none",
}));

const DesktopCheckoutSignIn = () => {
  const dispatch = useAppDispatch();

  const theme = useTheme();
  const lastSubmittedCodeRef = useRef<string>("");

  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [agreedToText, setAgreedToText] = useState<boolean>(false);

  const hasOtpSession = !!otpSession?.username;

  useEffect(() => {
    // Check if the phone number entered is different from the one in the session; if so, clear the session
    if (
      hasOtpSession &&
      AuthHelper.getEmailForPhoneNumber(phoneNumber) !== otpSession.username
    ) {
      setOtpSession(undefined);
    }
  }, [hasOtpSession, otpSession?.username, phoneNumber]);

  const {
    signIn,
    submittingSignIn,
    verifyingOtp,
    loadingProfile,
    otpError,
    resendCoolDown,
    verifyOtp,
    resendOtp,
    clearOtpError,
  } = useContext(UserLoginContext);

  const submitPhoneNumber = () => {
    const phoneNumberInput = formatPhone(phoneNumber, "e164");
    signIn(phoneNumberInput, false, (_, session) => {
      setOtpSession(session);
    });
  };

  const submitOTP = useCallback(
    (otp: string) => {
      verifyOtp(otp, otpSession, () => {
        dispatch(reloadProfile());
      });
    },
    [dispatch, otpSession, verifyOtp]
  );

  useEffect(() => {
    const OTP_EXPECTED_LENGTH = 6;
    if (
      verificationCode.length === OTP_EXPECTED_LENGTH &&
      verificationCode !== lastSubmittedCodeRef.current
    ) {
      lastSubmittedCodeRef.current = verificationCode;
      submitOTP(verificationCode);
    }
  }, [submitOTP, verificationCode]);

  const resendOTP = () => {
    resendOtp(phoneNumber, false, (session) => {
      setOtpSession(session);
    });
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: "16.3px",
        }}
      >
        <CheckoutInputV2
          label="Phone Number"
          name="phoneNumber"
          autoComplete="tel"
          value={phoneNumber}
          style={{
            minWidth: "300px",
          }}
          autoFocus
          disabled={submittingSignIn || verifyingOtp || loadingProfile}
          onChange={(e) => {
            const value = e.target.value;
            setPhoneNumber(value?.trim());
            // Clear any previous errors if needed
            clearOtpError();
          }}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            inputProps: {
              format: getPhoneNumberMasking(phoneNumber || ""),
            },
          }}
          endButton={
            otpSession ? (
              <OutlineButton
                onClick={() => {
                  setVerificationCode("");
                  clearOtpError();
                  resendOTP();
                }}
                disabled={
                  resendCoolDown > 0 || submittingSignIn || verifyingOtp
                }
              >
                Resend Code
              </OutlineButton>
            ) : (
              <SendCodeButton
                onClick={submitPhoneNumber}
                disabled={
                  submittingSignIn ||
                  verifyingOtp ||
                  !validatePhone(phoneNumber) ||
                  !agreedToText
                }
              >
                Send code
              </SendCodeButton>
            )
          }
          sx={{ flex: 1 }}
        />
        <CheckoutInputV2
          label="Verification Code"
          name="verificationCode"
          autoComplete="one-time-code"
          value={verificationCode}
          disabled={
            !otpSession || submittingSignIn || verifyingOtp || loadingProfile
          }
          style={{
            minWidth: "300px",
            visibility: otpSession ? "visible" : "hidden",
          }}
          autoFocus
          inputStyle={{
            color: otpError ? theme.palette.error.main : "inherit",
          }}
          onChange={(e) => {
            setVerificationCode(e.target.value);
            clearOtpError();
          }}
          sx={{ flex: 1 }}
          endButton={
            otpError || loadingProfile ? (
              <SuccessStatusIcon success={!otpError} />
            ) : null
          }
        />
      </Box>

      <Box sx={{ mt: "16.3px" }}>
        <Box
          display="flex"
          gap="8px"
          alignItems="center"
          width="100%"
          fontSize="14px"
        >
          <CheckoutToggle
            disabled={
              submittingSignIn || verifyingOtp || loadingProfile || !!otpSession
            }
            checked={agreedToText}
            onClick={() => setAgreedToText(!agreedToText)}
          />
          To continue, consent to receiving text messages about orders and
          marketing offers.
        </Box>
      </Box>
    </div>
  );
};

export default DesktopCheckoutSignIn;
